import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { authService } from '../services/authService';
import { firebaseAuthService } from '../services/firebaseAuthService';
import { updateUserProfile } from '../services/userService';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'LOAD_USER'; payload: User }
  | { type: 'UPDATE_USER'; payload: User };

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isLoading: true,
  isAuthenticated: false,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        isAuthenticated: true,
      };
    case 'LOGIN_FAILURE':
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
      };
    case 'LOGOUT':
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
      };
    case 'LOAD_USER':
      return {
        ...state,
        user: action.payload,
        isLoading: false,
        isAuthenticated: true,
      };
    case 'UPDATE_USER':
      localStorage.setItem('user', JSON.stringify(action.payload));
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

interface AuthContextType {
  state: AuthState;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (userData: {
    username: string;
    email: string;
    password: string;
    role?: string;
  }) => Promise<void>;
  logout: () => void;
  updateUser: (userData: {
    profile?: Partial<User['profile']>;
    preferences?: Partial<User['preferences']>;
  }) => Promise<User | undefined>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const loadUser = async () => {
      if (state.token) {
        try {
          // Try to get user from API first (most up-to-date)
          const response = await authService.getProfile();
          dispatch({ type: 'LOAD_USER', payload: response.user });
        } catch (error) {
          // If API call fails, try to load from localStorage
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              const user = JSON.parse(storedUser);
              dispatch({ type: 'LOAD_USER', payload: user });
              return;
            } catch (parseError) {
              console.error('Error parsing stored user:', parseError);
            }
          }
          // If everything fails, clear auth state
          dispatch({ type: 'LOGIN_FAILURE' });
        }
      } else {
        // Check if user is stored in localStorage but token is missing
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        
        if (storedToken && storedUser) {
          try {
            const user = JSON.parse(storedUser);
            dispatch({ 
              type: 'LOGIN_SUCCESS', 
              payload: { user, token: storedToken } 
            });
            return;
          } catch (parseError) {
            console.error('Error parsing stored user:', parseError);
          }
        }
        
        dispatch({ type: 'LOGIN_FAILURE' });
      }
    };

    loadUser();
  }, [state.token]);

  const login = async (email: string, password: string) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      const response = await authService.login({ email, password });
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: response.user, token: response.token },
      });
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      dispatch({ type: 'LOGIN_START' });
      const response = await firebaseAuthService.loginWithGoogle();
      
      // Transform the Firebase auth response to match our User type
      const user = {
        _id: response.user.id,
        id: response.user.id,
        username: response.user.username,
        email: response.user.email,
        role: response.user.role as 'student' | 'teacher' | 'admin',
        reputation: response.user.reputation,
        avatar: response.user.avatar,
        createdAt: new Date().toISOString(),
        savedQuestions: [],
        savedAnswers: [],
        activityLog: [],
        preferences: {
          emailNotifications: true,
          categories: [],
          difficultyLevel: 'beginner'
        },
        profile: {
          bio: '',
          location: '',
          website: '',
          learningGoals: [],
          nativeLanguage: '',
          englishLevel: 'beginner' as 'beginner' | 'intermediate' | 'advanced'
        }
      };
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user, token: response.token },
      });
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const register = async (userData: {
    username: string;
    email: string;
    password: string;
    role?: string;
  }) => {
    try {
      dispatch({ type: 'LOGIN_START' });
      const response = await authService.register(userData);
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: response.user, token: response.token },
      });
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    dispatch({ type: 'LOGOUT' });
  };

  const updateUser = async (userData: {
    profile?: Partial<User['profile']>;
    preferences?: Partial<User['preferences']>;
  }) => {
    if (!state.user) return;
    
    try {
      const response = await updateUserProfile(
        userData.profile || {},
        userData.preferences || {}
      );
      if (response.data) {
        dispatch({ type: 'UPDATE_USER', payload: response.data });
        return response.data;
      }
    } catch (error) {
      throw error;
    }
  };

  const value = {
    state,
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    login,
    loginWithGoogle,
    register,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
